import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Records soles that arrived for a sole order (adds them to sole stock)
export default function SoleReceiveDialog({ order, onOpenChange, onSaved }) {
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setQty({}); setNote(""); }, [order]);
  if (!order) return null;

  const rows = order.items.map((it, idx) => ({ ...it, idx, rem: it.qty - (it.received_qty || 0) })).filter((r) => r.rem > 0);
  const total = Object.values(qty).reduce((a, b) => a + (parseInt(b) || 0), 0);

  const fillAll = () => setQty(Object.fromEntries(rows.map((r) => [r.idx, r.rem])));

  const submit = async () => {
    const items = rows.filter((r) => (parseInt(qty[r.idx]) || 0) > 0)
      .map((r) => ({ sole_code: r.sole_code, sole_color: r.sole_color, size: r.size, qty: parseInt(qty[r.idx]) }));
    if (!items.length) { toast.error("Gelen adet girin"); return; }
    setSaving(true);
    try {
      await api.post(`/sole-orders/${order.id}/receive`, { note, items });
      toast.success(`${total} çift taban teslim alındı ve stoğa eklendi`);
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FFFBF4] border-[#DCCFB8] text-[#2B2118] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sole-receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase tracking-tight">Gelen Taban · Sipariş #{order.no}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B5B4A]">{order.supplier}</p>
        <div className="overflow-x-auto rounded-md border border-[#DCCFB8]">
          <table className="w-full text-sm zs-table">
            <thead className="bg-[#EFE6D6]">
              <tr>
                <th className="text-left px-3 py-2">Taban</th><th className="text-center px-3 py-2">N°</th>
                <th className="text-right px-3 py-2">Sipariş</th><th className="text-right px-3 py-2">Gelen</th>
                <th className="text-right px-3 py-2">Kalan</th><th className="text-center px-3 py-2">Şimdi Gelen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.idx} className="border-t border-[#E8DDCA]" data-testid={`sole-receive-row-${r.idx}`}>
                  <td className="px-3 py-2 font-mono text-[#B45309]">{r.sole_code}{r.sole_color ? ` · ${r.sole_color}` : ""}</td>
                  <td className="px-3 py-2 text-center font-mono">{r.size}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.qty}</td>
                  <td className="px-3 py-2 text-right font-mono text-[#047857]">{r.received_qty || 0}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-[#B45309]">{r.rem}</td>
                  <td className="px-3 py-2">
                    <Input type="number" min={0} max={r.rem} inputMode="numeric" placeholder="0" data-testid={`sole-receive-qty-${r.idx}`}
                      value={qty[r.idx] ?? ""}
                      onChange={(e) => setQty((p) => ({ ...p, [r.idx]: e.target.value === "" ? "" : Math.max(0, Math.min(r.rem, parseInt(e.target.value) || 0)) }))}
                      className="zs-size-input text-center h-9 w-20 mx-auto bg-[#EFE6D6] border-[#DCCFB8]" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button type="button" variant="outline" onClick={fillAll} data-testid="sole-receive-fill-all"
            className="border-[#DCCFB8] bg-[#EFE6D6] text-[#B45309] hover:bg-[#FFFBF4]">Kalanın Hepsi Geldi</Button>
          <Input placeholder="Not (irsaliye no vb.)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="sole-receive-note"
            className="h-10 bg-[#EFE6D6] border-[#DCCFB8]" />
        </div>
        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <p className="font-mono text-xl font-bold text-[#047857]" data-testid="sole-receive-total">{total} çift</p>
            <Button onClick={submit} disabled={saving || !total} data-testid="sole-receive-submit-btn"
              className="bg-[#D97706] hover:bg-[#B45309] text-white font-display uppercase tracking-wide h-11 px-6">
              {saving ? "Kaydediliyor..." : "Teslim Al"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
